// Orchestrator E2E Test Page — minimal harness to verify the full
// frontend ↔ API ↔ DB ↔ Kafka ↔ DB loop works end-to-end.
//
// What it does:
//   1. Lets you create a manual work item (POST /orchestrator/work-items)
//   2. Lets you ingest a manual PCN (POST /pcn/ingest)
//   3. Polls the inbox + counts and shows the live results
//   4. Lets you execute actions (Local + Kafka-mediated) on items
//
// Route: /orchestrator/e2e-test
//
// NOT meant for production users — intentionally raw, intentionally ugly.
// When the live worklist page promotes from the mock, this can be removed.
import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, TextField, Chip, Divider, Alert,
  Select, MenuItem, FormControl, InputLabel, useTheme, alpha,
} from '@mui/material';
import orchestratorService, {
  OrchestratorCounts,
  OrchestratorInboxResult,
  WorkItemRole,
} from '../../services/orchestratorService';

const TEST_TENANT_HINT = '1c484f59-7a52-41fc-9467-ad46d2dc30eb';

const OrchestratorE2ETestPage: React.FC = () => {
  const theme = useTheme();
  const [role, setRole] = useState<WorkItemRole>('buyer');
  const [inbox, setInbox] = useState<OrchestratorInboxResult | null>(null);
  const [counts, setCounts] = useState<OrchestratorCounts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastAction, setLastAction] = useState<string>('');

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, c] = await Promise.all([
        orchestratorService.list({ role, limit: 20 }),
        orchestratorService.counts(role),
      ]);
      setInbox(list);
      setCounts(c);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const createTestPcnWorkItem = async () => {
    setError(null);
    setLastAction('');
    try {
      const result = await orchestratorService.create({
        triggerType: 'PCN',
        taskType: 'PCN_RESPONSE',
        subjectType: 'PART',
        subjectId: `test-part-${Date.now()}`,
        subjectSnapshot: {
          name: 'PN-99999',
          partNumber: 'PN-99999',
          mpn: 'STM32F407VGT6',
          manufacturer: 'STMicroelectronics',
          description: 'MCU 32-bit ARM Cortex-M4 (e2e test)',
        },
        severity: 'CRITICAL',
        taskPayload: { source: 'e2e-test-page' },
      });
      setLastAction(`Created WI ${result.workItemId}`);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Create failed');
    }
  };

  const ingestTestPcn = async () => {
    setError(null);
    setLastAction('');
    try {
      const ltb = new Date();
      ltb.setDate(ltb.getDate() + 94);
      const result = await orchestratorService.ingestPcn({
        manufacturerName: 'STMicroelectronics',
        manufacturerPcnReference: `PCN-TEST-${Date.now()}`,
        affectedMpns: ['STM32F407VGT6'],
        changeType: 'EOL',
        changeDescription: 'E2E test PCN',
        ltbDate: ltb,
      });
      setLastAction(`Ingested PCN ${result.pcn?.pcnId}`);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ingest failed');
    }
  };

  const dispatchAction = async (workItemId: string, actionId: string, input: any) => {
    setError(null);
    setLastAction('');
    try {
      const result = await orchestratorService.executeAction(workItemId, actionId, input);
      setLastAction(
        `${actionId} on ${workItemId} → mode=${result.dispatchMode}` +
        (result.eventPublished ? ' (event published)' : ''),
      );
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Action failed');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{
        mb: 2, px: 2, py: 1, borderRadius: 1.5,
        bgcolor: alpha(theme.palette.warning.main, 0.08),
        border: '1px dashed', borderColor: alpha(theme.palette.warning.main, 0.4),
      }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          E2E TEST HARNESS
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          Verifies frontend → API → DB → Kafka loop. Use auth headers / JWT;
          tenant defaults to dev tenant {TEST_TENANT_HINT}.
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Orchestrator E2E Test</Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Role</InputLabel>
          <Select label="Role" value={role} onChange={(e) => setRole(e.target.value as WorkItemRole)}>
            <MenuItem value="buyer">Buyer</MenuItem>
            <MenuItem value="engineer">Engineer</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={refresh} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {lastAction && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLastAction('')}>
          {lastAction}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button variant="contained" onClick={createTestPcnWorkItem}>
          Create test PCN work item
        </Button>
        <Button variant="contained" color="secondary" onClick={ingestTestPcn}>
          Ingest test PCN doc
        </Button>
      </Stack>

      {/* Counts strip */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
          Counts
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`Total: ${counts?.total ?? 0}`} color="primary" />
          {counts && Object.entries(counts.counts).map(([k, v]) => (
            <Chip key={k} label={`${k}: ${v}`} variant="outlined" />
          ))}
        </Stack>
      </Paper>

      {/* Inbox list */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
            Inbox ({inbox?.total ?? 0} items)
          </Typography>
        </Box>
        {inbox?.items.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
            <Typography variant="body2">No work items yet — try creating one above.</Typography>
          </Box>
        )}
        {inbox?.items.map((wi: any) => (
          <Box key={wi.workItemId} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {wi.subjectSnapshot?.name} · {wi.triggerType} · {wi.severity}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {wi.subjectSnapshot?.manufacturer} · {wi.subjectSnapshot?.description}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                  WI: {wi.workItemId} · status: {wi.status} · owner: {wi.ownerRole} ·
                  {' '}assigned: {wi.assigneeUserId || '—'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  onClick={() => dispatchAction(wi.workItemId, 'ASSIGN_WORK_ITEM', {
                    assigneeUserId: 'e5ec6b3b-87e8-4919-8185-c6a088c0c9cb',
                    assigneeUserName: 'E2E Tester',
                  })}
                >
                  Assign me
                </Button>
                <Button
                  size="small"
                  color="success"
                  onClick={() => dispatchAction(wi.workItemId, 'RESOLVE_WORK_ITEM', {})}
                >
                  Resolve
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    dispatchAction(wi.workItemId, 'DISMISS_WORK_ITEM', { reason: 'e2e test' })
                  }
                >
                  Dismiss
                </Button>
                <Divider orientation="vertical" flexItem />
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={() =>
                    dispatchAction(wi.workItemId, 'APPROVE_ALT_AS_BUYER', {
                      partCrossReferenceId: 'test-xref-001',
                      rationale: 'e2e test approval',
                    })
                  }
                >
                  Buyer ✓ alt (Kafka)
                </Button>
              </Stack>
            </Stack>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default OrchestratorE2ETestPage;
